<template>
	<h2 class="popup-headline">{{ t('popup.media.titleTemplates') }}</h2>
	<alert-message
		v-if="alert.isShown"
		:alert="alert"
		@close-alert="alertHide"
		class="popup-alert"
	></alert-message>
	<expansion-panel
		v-for="(category, index) in mediaCategories"
		:panel-key="index"
		:key="category.id"
		:title="category[categoryLang]"
		v-model:panel-opened="panelOpenedKey"
		:mod-classname="'minimalistic media-templates-panel'"
	>
		<media-template-category
			:category-id="category.id"
			:assetsURL="assetsURL"
			:mediaURL="mediaURL"
			:map-id="currentMap.id"
			:is-choosing="isMediaChoosing"
			:active="panelOpenedKey === index"
			@chosen="getMediaURL"
		></media-template-category>
	</expansion-panel>
</template>

<script>
import {useStore} from 'vuex';
import {computed, ref} from 'vue';
import {ALERT_TYPES} from '@/models/ALERT_TYPES';
import useAlert from '@/utils/Alert';
import AlertMessage from '@/components/ui/alert/AlertMessage.vue';
import {useI18n} from 'vue-i18n';
import ExpansionPanel from '@/components/ui/panel/ExpansionPanel';
import useI18nGlobal from '@/utils/i18n';
import RestrictionUtils from "@/utils/client/RestrictionUtils";
import MediaTemplateCategory from '@/components/editor-options/settings/popup/content/media/MediaTemplateCategory.vue';

export default {
	components: {
		ExpansionPanel,
		AlertMessage,
		MediaTemplateCategory
	},
	props: {
		popupContentRef: {
			default: null
		}
	},
	emits: ["chosenMedia"],
	setup(props, {emit}) {
		const {t} = useI18n();
		const baseURL = import.meta.env.VITE_ROOT_API;
		const store = useStore();
		const currentMap = computed(() => store.getters['maps/getCurrentMap'])
		const assetsURL = computed(() => store.getters['mediaAssets/getAssetsURL']);
		const mediaURL = computed(() => `${baseURL}${assetsURL.value}`);
		// alert
		const { alert, alertHide, alertShow, alertCreate } = useAlert();
		
		const popupContentReference = ref(props.popupContentRef);
		
		const isMediaChoosing = computed(() => store.getters["settingsPopup/isMediaChoosing"]);
		
		// CATEGORIES
		const {getCurrentLang} = useI18nGlobal();
		const categoryLang = ref(getCurrentLang());
		
		async function getMediaURL(media){
			const chosenMediaURL = `${mediaURL.value}${media.key}`
			if(isMediaChoosing.value){
				emit("chosenMedia", {url: chosenMediaURL, name: media.filename, type: media.mime_type});
			} else {
				await navigator.clipboard.writeText(chosenMediaURL);
				alertCreate({type: ALERT_TYPES.SUCCESS, message: t('popup.media.message.paste')});
				alertShow();
				setTimeout(() => alertHide(), 2000);
			}
		}
		
		// EXPANSION PANEL
		const panelOpenedKey = ref(null);
		
		// FETCH DATA
		const clientFeatures = computed(() => store.getters["clients/getClientFeatures"]);
		const mediaCategories = computed(() => clientFeatures.value.media_template_categories);
		const clientName = computed(() => store.getters["user/getClientName"]);
		async function fetchFeatures(clientSlug){
			// fetch client features to update media categories; each category
			// panel then pages its own assets
			const features = await store.dispatch('clients/fetchFeatures', clientSlug);
			const {getRestrictions} = RestrictionUtils();
			await store.dispatch('clients/restrictionsSet', getRestrictions(features));
		}
		
		// PROCESS
		fetchFeatures(clientName.value);
		
		return{
			t,
			alert,
			alertHide,
			mediaURL,
			assetsURL,
			currentMap,
			getMediaURL,
			isMediaChoosing,
			categoryLang,

			panelOpenedKey,
			mediaCategories
		}
		
	}
}
</script>

<style lang="scss">
@use "@/scss/items/assets/assets-list";
@use "@/scss/items/assets/assets-item";
</style>
